import React, { useState, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import Tooltip from "aldoo-ra/Tooltip"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Icon from "aldoo-ra/Icon"
import { priceDisplayFormat } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import { twMerge } from "tailwind-merge"
import { getProductTypeColor } from "../Products/store-product-utils"

import apiInterface from "aldoo-ra/CMSModels/StoreProduct/api"
const { GetDeleted, Restore, HardDelete } = apiInterface

export default function StoreProductRestoreDialog({ isOpen, onClose, onRefresh }) {
  const [refreshKey, setRefreshKey] = useState(0)
  const [deletedItems, setDeletedItems] = useState([])

  // Refresh the table when dialog opens
  useEffect(() => {
    if (!isOpen) return
    setRefreshKey((prev) => prev + 1)
  }, [isOpen])

  // Handler for deleting an item forever
  const deleteForeverAction = (item) => {
    showPromptDialog({
      variant: "admin",
      title: "Delete Forever",
      message: `Are you sure you want to permanently delete product ${item.pid}? This action cannot be undone.`,
      confirmText: "Delete Forever",
      onConfirm: async () => {
        try {
          await HardDelete(item) // Call HardDelete with item
          showNotification({
            message: "Product permanently deleted successfully.",
            className: "bg-admin_primary text-admin_text_inv",
          })
          // Force refresh the restore dialog table
          setTimeout(() => {
            setRefreshKey((prev) => prev + 1)
          }, 50)
          onRefresh?.() // Refresh the main table
        } catch (error) {
          showNotification({
            message: "Failed to delete the product.",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handler for restoring an item
  const restoreAction = async (item) => {
    try {
      await Restore(item._id) // Restore product
      showNotification({
        message: "Successfully restored the product.",
        className: "bg-admin_primary text-admin_text_inv",
      })
      // Force refresh the restore dialog table
      setTimeout(() => {
        setRefreshKey((prev) => prev + 1)
      }, 50)
      onRefresh?.() // Refresh the main table
    } catch (error) {
      showNotification({
        message: "Failed to restore the product.",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handler for deleting all items forever
  const deleteAllAction = async () => {
    try {
      // Fetch ALL deleted items in batches (no limit)
      let allDeletedItems = []
      let skip = 0
      const batchSize = 1000
      let hasMore = true

      while (hasMore) {
        const response = await GetDeleted({
          page: Math.floor(skip / batchSize) + 1,
          itemsPerPage: batchSize,
          search: {},
          sort: { deletedAt: -1 }
        })

        const items = response?.data || []

        if (Array.isArray(items) && items.length > 0) {
          allDeletedItems = allDeletedItems.concat(items)

          if (items.length < batchSize) {
            hasMore = false
          } else {
            skip += batchSize
          }
        } else {
          hasMore = false
        }
      }

      if (allDeletedItems.length === 0) {
        showNotification({
          message: "No deleted products to remove.",
          className: "bg-admin_error text-white",
        })
        return
      }

      showPromptDialog({
        variant: "admin",
        title: "Delete All Forever",
        message: `Are you sure you want to permanently delete all ${allDeletedItems.length} product(s)? This action cannot be undone and there is no way to reverse it.`,
        confirmText: "Delete All Forever",
        onConfirm: async () => {
          try {
            // Delete all items one by one
            const deletePromises = allDeletedItems.map(item => HardDelete(item))
            await Promise.all(deletePromises)

            showNotification({
              message: `Successfully deleted ${allDeletedItems.length} product(s) permanently.`,
              className: "bg-admin_primary text-admin_text_inv",
            })

            // Force refresh the restore dialog table
            setTimeout(() => {
              setRefreshKey((prev) => prev + 1)
            }, 50)
            onRefresh?.() // Refresh the main table
          } catch (error) {
            showNotification({
              message: "Failed to delete all products.",
              className: "bg-admin_error text-white",
            })
          }
        },
      })
    } catch (error) {
      showNotification({
        message: "Failed to fetch deleted products.",
        className: "bg-admin_error text-white",
      })
    }
  }

  const formatPrice = (priceItem) => {
    if (!priceItem) return "No price model"

    //the primary price model
    const priceModel = priceItem.price_model?.find((c) => c.primary)

    if (!priceModel) return "No price model"

    let price = priceDisplayFormat(priceModel)

    if (price) {
      return (
        <div className="flex flex-col">
          <div className="font-semibold">{price}</div>
          <div className="text-admin_text_secondary">{priceItem.price_id}</div>
        </div>
      )
    }

    return "Invalid currency"
  }

  // Define columns for the data table
  const tableItemRender = [
    {
      id: "pid",
      title: "PID",
      headerClassName: "w-[25%]",
      render: (value, item) => (
        <span className="text-admin_text">{item.pid}</span>
      ),
    },
    {
      id: "price_model",
      title: "Price",
      headerClassName: "w-[10%]",
      render: (value, item) => {
        return (
          <div className="flex flex-col gap-1">
            <Tooltip
              content={
                item.price?.type === "real" ? (
                  <div>
                    {item.price.real_product_id?.map((platform, index) => (
                      <div key={platform?.id || index + Math.random()}>
                        {platform.platform}: {platform.id}
                      </div>
                    ))}
                  </div>
                ) : null
              }
            >
              <div className="flex flex-col justify-center gap-2">
                <div className="">{formatPrice(item.price)}</div>
                {item.price?.price_model?.length > 1 && (
                  <span className="text-secondary">
                    +{item.price.price_model.length - 1} more
                  </span>
                )}
              </div>
            </Tooltip>
          </div>
        )
      },
    },
    {
      id: "type",
      title: "Type",
      headerClassName: "w-[15%]",
      render: (value, item) => {
        const typeColor = getProductTypeColor(item.type)

        return (
          <span className={twMerge("px-2 py-1 rounded-full", typeColor)}>
            {item.type}
          </span>
        )
      },
    },
    {
      id: "platforms",
      title: "Platforms",
      headerClassName: "w-[15%]",
      render: (value, item) => {
        const { price } = item
        if (!price?.price_model) return <div className="flex flex-row gap-2">N/A</div>

        //get only the unique platforms within the currency products
        const unique = [
          ...new Set(
            price.price_model
              .filter((c) => c.type === "real")
              .map((c) => c.real_product_id?.map((p) => p.platform) || [])
              .flat()
          ),
        ] //alphabetically sort the platforms
          .sort((a, b) => a.localeCompare(b))

        //show the available platforms that have real currency, show as icons
        return (
          <div className="flex flex-row gap-2">
            {unique.map((item, index) => (
              <div key={item?.id || index}>
                <Tooltip content={item}>
                  <Icon
                    id={`admin_${item.toLowerCase()}`}
                    className="w-7 h-7"
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        )
      },
    },
    {
      id: "enabled",
      title: "Enabled",
      headerClassName: "w-[10%] text-center",
      render: (value, item) => (
        <div className="flex items-center justify-center">
          <ToggleSwitch
            isOn={item.enabled}
            disabled={true} // Disabled since it's just for display
          />
        </div>
      ),
    },
    {
      id: "restore",
      headerClassName: "w-[10%]",
      render: (value, item) => (
        <Button
          variant="admin_secondary"
          className="mx-auto"
          onClick={() => restoreAction(item)}
        >
          Restore
        </Button>
      ),
    },
    {
      id: "delete",
      headerClassName: "w-[15%]",
      render: (value, item) => (
        <Button
          variant="admin_error"
          className="whitespace-nowrap mx-auto"
          onClick={() => deleteForeverAction(item)}
        >
          Delete Forever
        </Button>
      ),
    },
  ]

  const headerControls = (
    <div className="absolute top-[45px] right-[350px] xl:right-[410px] flex items-center gap-3">
      <Button
        variant="admin_error"
        onClick={deleteAllAction}
        className="whitespace-nowrap"
      >
        Delete All{deletedItems.length > 0 ? ` (${deletedItems.length})` : ''}
      </Button>
    </div>
  )

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      className="md:absolute w-full md:w-[95%] h-full md:h-auto top-[15px]"
      zIndex={200}
    >
      <div className="p-4">
        <Typography variant="admin_h3" className="mb-4">
          Restore Deleted Products
        </Typography>

        {/* Data table for displaying deleted items */}
        <div className="h-[75vh]">
          {" "}
          {/* Set a minimum height */}
          <DataTable
            variant="admin"
            refreshKey={refreshKey}
            apiMethod={GetDeleted}
            preferredHeight="75vh"
            itemHeight="h-[65px] xl:h-[56px]"
            localUserPreferencesKey="admin.store.restoreProducts"
            headerControls={headerControls}
            styleOverrides={{
              searchInput: "absolute top-[45px] right-[40px] w-full max-w-[300px] xl:max-w-[360px] ml-6 xl:ml-8",
              table: "w-full table-fixed",
            }}
            searchField="name pid type"
            requestFields={{}}
            searchObject={{}}
            sort={{ deletedAt: -1 }}
            renders={tableItemRender}
            onDataLoaded={(data) => {
              setDeletedItems(data || [])
              return data
            }}
            noData={
              <div className="min-h-[400px] flex items-center justify-center">
                <Typography variant="paragraph" className="text-admin_text">
                  No deleted products found
                </Typography>
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  )
}